import React, { useRef, useEffect, useState, useMemo } from "react";
import './styles.css';
import {
  List, Button, Input, message, Typography, Grid, Drawer, Space, Modal as AntModal,
  Select, ConfigProvider
} from 'antd';
import ItemCardCarrito from '../../components/ventas/ItemCardCarrito';
import { useGetProductos } from "../../hooks/useProductos";
import { useGetStockBodega, useGetBodegas } from "../../hooks/useBodegas";
import { useVentasEspera } from "../../hooks/useVentasEspera";
import { useCaja } from "../../hooks/useCaja";
import { useGetUsuarios } from "../../hooks/useUsuarios";
import {
  SwapOutlined, FileTextOutlined, ThunderboltOutlined, DownOutlined, ShoppingCartOutlined,
  ToolOutlined, UserOutlined
} from '@ant-design/icons';
import { IoPricetagsOutline } from "react-icons/io5";
import { useReactToPrint } from 'react-to-print';
import { TicketVenta } from './TicketVenta';
import { useConfig } from "../../context/ConfigContext";
import SeccionVentasEspera from "./sections/SeccionVentasEspera";
import SeccionListaVentas from "./sections/SeccionListaVentas";
import SeccionVentasPrincipal from "./sections/SeccionVentasPrincipal";
import ModalPausarVenta from "./modals/ModalPausarVenta";
import ModalClienteRapido from "./modals/ModalClienteRapido";
import ModalCobrar from "./modals/ModalCobrar";
import LayoutHome from "../../components/layout/LayoutHome";
import { usePermission } from "../../hooks/usePermission";
import axios from "axios";
import { useAuth } from "../../aws/AuthContext";
import ModalServicios from "./modals/ModalServicios";
import ModalVariantes from "./modals/ModalVariantes";
import VentanaCajaCerrada from "./ventanas/VentanaCajaCerrada";
import VentanaVerificandoCaja from "./ventanas/VentanaVerificandoCaja";
import ModalSeleccionarClientePedido from "./modals/ModalSeleccionarClientePedido";
import es_ES from 'antd/locale/es_ES';
import { useZonas } from "../../hooks/useZonas";
import ModalConfirmarPedido from "./modals/ModalConfirmarPedido";
import { useLocation } from 'react-router-dom';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const Ventas = () => {

  const { getJwtToken } = useAuth();
  const { hasPermission, permisos = [] } = usePermission();
  const { config } = useConfig();
  const location = useLocation();

  const posPermiteEdicion = config.settings?.permitir_edicion_precios === 'true' || config.settings?.permitir_edicion_precios === true;
  const puedeEditar = posPermiteEdicion && hasPermission('ventas:editar_precio');
  const controlarStock = config.settings?.controlar_stock === 'true' || config.settings?.controlar_stock === true;
  const moduloPedidosActivo = config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true';
  const estaAsignadoEnRol = permisos.includes('pedidos:crear') || permisos.includes('pedidos:gestionar');
  const mostrarSwitcherPedidos = moduloPedidosActivo && estaAsignadoEnRol;

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Ticket de Venta",
  });

  const { zonas = [] } = useZonas();

  // --- DETECCIÓN MÓVIL ---
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [drawerCarritoVisible, setDrawerCarritoVisible] = useState(false);

  const { getCajaActiva } = useCaja();
  const cajaActiva = getCajaActiva.data;
  const isLoadingCaja = getCajaActiva.isLoading;

  const { data: bodegas = [] } = useGetBodegas();
  const bodegaPrincipalObj = bodegas.find(b => b.es_default === true || b.es_default === 'true');

  const { data: usuariosReales = [], isLoading: cargandoUsuarios } = useGetUsuarios();

  const bodegaIdFinal = cajaActiva?.bodega_id || bodegaPrincipalObj?.id;
  const bodegaActivaObj = bodegas.find(b => b.id === bodegaIdFinal) || bodegaPrincipalObj;

  const { data: productosDB = [] } = useGetProductos();
  const { data: stockBodegasDB = [] } = useGetStockBodega(bodegaIdFinal);

  const productosActivos = useMemo(() => {
    const esRutaVendedor = bodegaActivaObj?.tipo === 'vendedor';

    return productosDB
      .filter(producto => producto.status === 'active' && !producto.ocultar_en_ventas)
      .map(producto => {
        // 1. Combos: calcula cuántos se pueden armar en esta bodega
        if (producto.es_combo) {
          let maxArmables = Infinity;
          let tieneIngredientes = false;
          if (producto.combo_items && producto.combo_items.length > 0) {
            producto.combo_items.forEach(ing => {
              const reg = stockBodegasDB.find(sb =>
                sb.producto_id === ing.producto_id &&
                (ing.variante_id ? sb.variante_id === ing.variante_id : !sb.variante_id)
              );
              if (reg) tieneIngredientes = true;
              const fisico = reg ? Number(reg.stock) : 0;
              const posibles = Math.floor(fisico / Number(ing.cantidad));
              if (posibles < maxArmables) maxArmables = posibles;
            });
          } else {
            maxArmables = 0;
          }
          return { ...producto, stock: maxArmables === Infinity ? 0 : maxArmables, existeEnBodega: tieneIngredientes };
        }

        // 2. Variantes: lee el stock exclusivo de esta bodega
        if (producto.has_variants && producto.variantes && producto.variantes.length > 0) {
          let algunaExiste = false;
          const variantesActivas = producto.variantes
            .filter(v => v.status === 'active')
            .map(v => {
              const stockVarFound = stockBodegasDB.find(sb => sb.producto_id === producto.id && sb.variante_id === v.id);
              if (stockVarFound) algunaExiste = true;
              return { ...v, stock: stockVarFound ? Number(stockVarFound.stock) : 0, existeEnBodega: !!stockVarFound };
            })
            .filter(v => !esRutaVendedor || v.existeEnBodega);

          const stockTotalPadre = variantesActivas.reduce((acc, v) => acc + v.stock, 0);
          return { ...producto, stock: stockTotalPadre, variantes: variantesActivas, existeEnBodega: algunaExiste };
        }

        // 3. Producto simple: lee el stock de esta bodega (0 si no existe)
        const stockFound = stockBodegasDB.find(sb => sb.producto_id === producto.id && !sb.variante_id);
        const stockReal = stockFound ? Number(stockFound.stock) : 0;

        return { ...producto, stock: stockReal, existeEnBodega: !!stockFound };
      })
      .filter(producto => {
        if (producto.has_variants && (!producto.variantes || producto.variantes.length === 0)) return false;

        const esBodegaPrincipal = bodegaActivaObj?.es_default === true || bodegaActivaObj?.es_default === 'true';
        if (!esBodegaPrincipal) return producto.existeEnBodega;

        return true;
      });
  }, [productosDB, stockBodegasDB, bodegaActivaObj]);

  // Extraemos las categorías automáticamente
  const categoriasUnicas = useMemo(() => {
    const cats = new Set(productosActivos.map(p => p.categoria).filter(Boolean));
    return ['Todos', ...Array.from(cats)];
  }, [productosActivos]);

  const [carrito, setCarrito] = useState([]);
  const [carritoActualizado, setCarritoActualizado] = useState([]);
  const [efectivo, setEfectivo] = useState();
  const [digitoComprobante, setDigitoComprobante] = useState();
  const [impuestos, setImpuestos] = useState(0);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Todos');
  const [busqueda, setBusqueda] = useState('');
  const [seccionActual, setSeccionActual] = useState('principal');
  const { getEspera, saveEspera, deleteEspera } = useVentasEspera();
  const isGuardandoEnEspera = saveEspera.isPending;
  const ventasEnEspera = Array.isArray(getEspera.data) ? getEspera.data : [];
  const [ventaActivaEnEspera, setVentaActivaEnEspera] = useState(null);
  const [isModalPausarOpen, setIsModalPausarOpen] = useState(false);
  const [nombreReferenciaPausar, setNombreReferenciaPausar] = useState('');
  const [isModalClienteOpen, setIsModalClienteOpen] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState({ id: 'DEFAULT', lista_precio_id: null });
  const [datosImpresion, setDatosImpresion] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPausarVenta, setIsPausarVenta] = useState(false);
  const [isModalVariantesOpen, setIsModalVariantesOpen] = useState(false);
  const [productoParaVariantes, setProductoParaVariantes] = useState(null);
  const [catalogoServicios, setCatalogoServicios] = useState([]);
  const [listaPrecioActiva, setListaPrecioActiva] = useState('DEFAULT');
  const [isModalServicioOpen, setIsModalServicioOpen] = useState(false);
  const [servTipo, setServTipo] = useState(null);
  const [servNombre, setServNombre] = useState('');
  const [servCosto, setServCosto] = useState(0);
  const [servPrecio, setServPrecio] = useState(0);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [isModoPedido, setIsModoPedido] = useState(false); // Controla el switch Venta/Pedido
  const [isModalPedidoOpen, setIsModalPedidoOpen] = useState(false);
  const [modoOperacion, setModoOperacion] = useState('venta'); // 'venta' | 'pedido'
  const [zonaActiva, setZonaActiva] = useState(null); // Guarda la zona elegida durante la jornada
  const [isModalSeleccionarClientePedidoOpen, setIsModalSeleccionarClientePedidoOpen] = useState(false);
  const [isModalConfirmarPedidoOpen, setIsModalConfirmarPedidoOpen] = useState(false);
  const [ordenIdOrigen, setOrdenIdOrigen] = useState(null);

  const servGanancia = (servPrecio || 0) - (servCosto || 0);
  const servMargen = servPrecio > 0 ? ((servGanancia / servPrecio) * 100).toFixed(1) : 0;

  // Efecto para restaurar la zona desde localStorage al cargar la vista
  useEffect(() => {
    if (modoOperacion === 'pedido' && zonas.length > 0) {
      const savedZonaId = localStorage.getItem('yugonub_zona_activa');
      if (savedZonaId) {
        const encontrada = zonas.find(z => Number(z.id) === Number(savedZonaId));
        if (encontrada) setZonaActiva(encontrada);
      }
    }
  }, [modoOperacion, zonas]);

  // 1. EFECTO ÚNICO PARA CARGAR DATOS AL MONTAR EL COMPONENTE
  useEffect(() => {
    if (location.state?.esEntregaPedido) {
      const { cliente, carrito: carritoPedido, ordenId } = location.state;

      if (cliente) {
        setClienteSeleccionado(cliente);
      }

      if (carritoPedido && carritoPedido.length > 0) {
        // Mapeamos los campos del pedido para adaptar a la estructura de la tarjeta del carrito en POS
        const carritoFormateado = carritoPedido.map((item) => ({
          ...item,
          id: item.id || (item.variante_id ? `${item.producto_id}_${item.variante_id}` : item.producto_id),
          numItems: Number(item.numItems || item.cantidad || 1),
          precioVenta: Number(item.precioVenta || item.precio_unitario || item.precio || 0),
          precioPorDefecto: Number(item.precioPorDefecto || item.precio_unitario || item.precio || 0),
          descuento: Number(item.descuento || 0)
        }));

        setCarrito(carritoFormateado);
        localStorage.setItem('yugonub_carrito', JSON.stringify(carritoFormateado));
      }

      if (ordenId) {
        setOrdenIdOrigen(ordenId);
      }

      if (isMobile) {
        setDrawerCarritoVisible(true);
      }

      message.info(`Orden #${ordenId} cargada en caja para cobro.`);
    } else {
      const temp = localStorage.getItem('yugonub_carrito');
      if (temp) {
        try {
          setCarrito(JSON.parse(temp));
        } catch (e) {
          console.error("Error al cargar carrito local", e);
        }
      }
    }
  }, [location.state, isMobile]);

  // 2. EFECTO PARA SINCRONIZAR CAMBIOS DEL CARRITO EN LOCALSTORAGE
  useEffect(() => {
    localStorage.setItem('yugonub_carrito', JSON.stringify(carrito));
  }, [carrito]);

  const productosFiltrados = useMemo(() => {
    let filtrados = productosActivos;

    if (busqueda.trim() !== '') {
      const term = busqueda.toLowerCase();
      return filtrados.filter(p => {
        // 1. Buscamos en el producto principal
        const coincidePadre = p.nombre?.toLowerCase().includes(term) ||
          p.codigo_barras?.toString().toLowerCase().includes(term) ||
          p.codigo_interno?.toString().toLowerCase().includes(term);

        // 2. Buscamos dentro de sus variantes (si tiene)
        const coincideVariante = p.has_variants && p.variantes && p.variantes.some(v =>
          v.codigo_barras?.toString().toLowerCase().includes(term) ||
          v.codigo_interno?.toString().toLowerCase().includes(term) ||
          v.atributos?.nombre?.toLowerCase().includes(term)
        );

        return coincidePadre || coincideVariante;
      });
    }

    if (categoriaSeleccionada !== 'Todos') {
      return filtrados.filter(p => p.categoria === categoriaSeleccionada);
    }

    return filtrados;
  }, [productosActivos, categoriaSeleccionada, busqueda]);

  const manejarCambioBusqueda = (valor) => {
    setBusqueda(valor); // Actualiza el filtro de los productos

    if (searchInputRef.current && searchInputRef.current.input) {
      const inputElement = searchInputRef.current.input;

      // Truco definitivo para forzar a React a limpiar un input no controlado
      const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(inputElement, valor);
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const { subtotal, totalDescuento, sumaTotalGlobal } = useMemo(() => {
    let sumaSubtotal = 0;
    let sumaDescuentos = 0;

    carrito.forEach(item => {
      const cantidad = Number(item.numItems) || 0;
      const precioBase = Number(item.precioVenta) || 0;
      const porcentajeDescuento = Number(item.descuento) || 0;

      const subtotalItem = precioBase * cantidad;
      const ahorroItem = subtotalItem * (porcentajeDescuento / 100);

      sumaSubtotal += subtotalItem;
      sumaDescuentos += ahorroItem;
    });

    return {
      subtotal: Math.round(sumaSubtotal),
      totalDescuento: Math.round(sumaDescuentos),
      sumaTotalGlobal: Math.round(sumaSubtotal - sumaDescuentos + impuestos)
    };
  }, [carrito, impuestos]);

  const cambio = useMemo(() => {
    if (efectivo === undefined || efectivo === '' || efectivo === 0) return undefined;

    const numEfectivo = Number(efectivo.toString().replace(/[^0-9]/g, '')) || 0;
    const totalNumerico = sumaTotalGlobal;

    const saldo = numEfectivo - totalNumerico;
    return saldo.toLocaleString('COP').replaceAll(',', '.');
  }, [efectivo, sumaTotalGlobal]);

  /*useEffect(() => {
    const temp = localStorage.getItem('yugonub_carrito');
    if (temp) {
      setCarrito(JSON.parse(temp));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('yugonub_carrito', JSON.stringify(carrito));
  }, [carrito]);*/

  useEffect(() => {
    if (ventaActivaEnEspera && isMobile) {
      setDrawerCarritoVisible(true);
    }
  }, [ventaActivaEnEspera, isMobile]);

  useEffect(() => {
    if (seccionActual === 'principal' && !isMobile && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 200); // Para asegurar que el componente ya se renderizó
    }
  }, [seccionActual, isMobile]);

  const pausarVenta = () => {

    if (carrito.length === 0) return message.warning("El carrito está vacío");
    if (isMobile) setDrawerCarritoVisible(false);
    if (ventaActivaEnEspera?.nombre_referencia) {
      ejecutarGuardadoEnEspera(ventaActivaEnEspera.nombre_referencia);
    } else {
      setNombreReferenciaPausar('');
      setIsModalPausarOpen(true);
    }
  };

  /**
   * @function vaciarCarrito 
   * @author Alexis Ruiz Uribe
   * @date 14/02/2024
   * @param 
   * @resumen Función para vaciar el carrito y los datos de la venta
   */
  const vaciarCarrito = () => {
    setCarrito([]);
    setEfectivo();
    setDigitoComprobante();
    setVentaActivaEnEspera(null);
    setOrdenIdOrigen(null);
    if (isMobile) setDrawerCarritoVisible(false);

    // Para que en moviles no abra el teclado
    if (!isMobile && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 500); // 500ms asegura que el modal de cobro ya se cerró completamente
    }
  }

  // Esta función decide si agrega directo o abre el modal
  const procesarSeleccionProducto = (producto, varianteDirecta = null) => {
    if (varianteDirecta) {
      agregarAlCarritoFinal(producto, varianteDirecta);
    } else if (producto.has_variants && producto.variantes && producto.variantes.length > 0) {
      setProductoParaVariantes(producto);
      setIsModalVariantesOpen(true);
    } else {
      agregarAlCarritoFinal(producto, null);
    }
  };

  const agregarAlCarritoFinal = (producto, variante) => {
    const cartItemId = variante ? `${producto.id}_${variante.id}` : producto.id;
    const stockDisponible = Number(variante ? variante.stock : producto.stock) || 0;

    // --- NUEVO: VALIDACIÓN ESTRICTA DE STOCK ---
    const existeEnCarrito = carrito.find(item => item.id === cartItemId);
    const cantidadActual = existeEnCarrito ? Number(existeEnCarrito.numItems) : 0;

    if (controlarStock && (cantidadActual + 1) > stockDisponible) {
      message.warning(`Stock insuficiente. No puedes agregar este producto.`);
      setIsModalVariantesOpen(false);
      return; // Rompemos el ciclo, no se agrega.
    }

    const listaFinal = (clienteSeleccionado.id !== 'DEFAULT' && clienteSeleccionado.lista_precio_id)
      ? clienteSeleccionado.lista_precio_id
      : listaPrecioActiva;

    const preciosAdicionales = {
      lista_1: variante ? variante.precio_lista_1 : (producto.variantes?.[0]?.precio_lista_1 || null),
      lista_2: variante ? variante.precio_lista_2 : (producto.variantes?.[0]?.precio_lista_2 || null),
      lista_3: variante ? variante.precio_lista_3 : (producto.variantes?.[0]?.precio_lista_3 || null)
    };
    const precioPorDefecto = variante ? (variante.precio_base || producto.precio_venta) : producto.precio_venta;

    // 3. Aplicar lista si existe, si no, ir al precio base
    const precioAplicar = (listaFinal !== 'DEFAULT' && preciosAdicionales[listaFinal])
      ? Number(preciosAdicionales[listaFinal])
      : precioPorDefecto;

    const nombreFinal = variante ? `${producto.nombre} (${variante.atributos?.nombre || 'Var'})` : producto.nombre;
    const descuentoAplicar = variante && variante.descuento !== undefined && variante.descuento !== null
      ? variante.descuento
      : (producto.descuento || 0);

    const precioCompra = variante && variante.precio_compra !== undefined && variante.precio_compra !== null
      ? Number(variante.precio_compra)
      : Number(producto.precio_compra || 0);

    setCarrito((prevCarrito) => {
      const existe = prevCarrito.find((item) => item.id === cartItemId);
      if (existe) {
        return prevCarrito.map((item) =>
          item.id === cartItemId ? { ...item, numItems: Number(item.numItems) + 1 } : item
        );
      }
      return [
        ...prevCarrito,
        {
          id: cartItemId,
          producto_id: producto.id,
          variante_id: variante ? variante.id : null,
          codigo_barras: variante?.codigo_barras || producto.codigo_barras,
          nombre: nombreFinal,
          precioVenta: precioAplicar,
          precioPorDefecto: precioPorDefecto, // Guardamos la base por si el cajero quita al cliente después
          preciosAdicionales: preciosAdicionales, // Guardamos la lista en el carrito para recalcular dinámicamente
          numItems: 1,
          stock: stockDisponible,
          descuento: descuentoAplicar,
          precio_compra: precioCompra
        },
      ];
    });
    setIsModalVariantesOpen(false);

    // Cada que se agregue un producto se tira el cursor al buscador
    if (!isMobile && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  };

  const handleAbrirServicio = () => {
    const moduloActivo = config.modulo_servicios_activo === true || config.modulo_servicios_activo === 'true';

    if (moduloActivo) {
      setIsModalServicioOpen(true);
    } else {
      AntModal.info({
        title: 'Módulo de Servicios Inactivo',
        content: 'Este es un módulo adicional. Comunícate con nosotros para empezar a facturar tus servicios en el sistema sin afectar tu inventario.',
        okText: 'Entendido',
        okButtonProps: { style: { backgroundColor: '#22BABB' } }
      });
    }
  };

  const agregarServicioAlCarrito = () => {
    if (!servTipo || !servNombre || servPrecio <= 0) {
      return message.warning("El tipo, nombre y precio de venta son obligatorios.");
    }
    if (servPrecio < servCosto) {
      return message.error("El precio de venta no puede ser menor al costo.");
    }

    const nuevoServicio = {
      id: `serv_${Date.now()}`, // ID temporal único
      producto_id: null, // Es un servicio, no un producto
      variante_id: null,
      codigo_barras: 'SERVICIO',
      nombre: `[${servTipo}] ${servNombre}`,
      precioVenta: servPrecio,
      precioPorDefecto: servPrecio,
      preciosAdicionales: null, // Los servicios no usan listas de precios de clientes
      numItems: 1,
      stock: 999, // Stock infinito
      descuento: 0,
      precio_compra: servCosto,
      precio_original: servPrecio,
      precio_editado: false,
      es_servicio: true // Bandera clave
    };

    setCarrito(prev => [...prev, nuevoServicio]);

    // Limpiar y cerrar
    setServTipo(null);
    setServNombre('');
    setServCosto(0);
    setServPrecio(0);
    setIsModalServicioOpen(false);
    message.success("Servicio agregado al carrito");
  };

  // RECALCULAR PRECIOS SI EL CAJERO CAMBIA EL CLIENTE O LA LISTA MANUAL
  useEffect(() => {
    if (carrito.length === 0) return;

    const listaFinal = (clienteSeleccionado.id !== 'DEFAULT' && clienteSeleccionado.lista_precio_id)
      ? clienteSeleccionado.lista_precio_id
      : listaPrecioActiva;

    let huboCambios = false;

    const nuevoCarrito = carrito.map(item => {

      if (item.precio_editado) return item;
      if (!item.preciosAdicionales) return item; // Si es un item viejo sin el objeto, se omite

      const nuevoPrecio = (listaFinal !== 'DEFAULT' && item.preciosAdicionales[listaFinal])
        ? Number(item.preciosAdicionales[listaFinal])
        : (item.precioPorDefecto || item.precioVenta);

      if (item.precioVenta !== nuevoPrecio) {
        huboCambios = true;
        return { ...item, precioVenta: nuevoPrecio };
      }
      return item;
    });

    if (huboCambios) setCarrito(nuevoCarrito);

  }, [clienteSeleccionado, listaPrecioActiva]);

  useEffect(() => {
    if (isModalServicioOpen) {
      const cargarCatalogo = async () => {
        try {
          const token = await getJwtToken();
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/v1-tipos-servicio`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Filtramos para que solo aparezcan los servicios con estado = true (Activos)
          setCatalogoServicios(res.data.filter(s => s.estado === true));
        } catch (error) {
          console.error("Error al cargar catálogo de servicios", error);
        }
      };
      cargarCatalogo();
    }
  }, [isModalServicioOpen]);

  // FUNCIÓN PARA AUTO-COMPLETAR DATOS AL SELECCIONAR DEL SELECT
  const handleSelectServicio = (value, option) => {
    setServTipo(value);

    // Si la descripción está vacía, le ponemos el nombre del servicio para ahorrar tiempo
    if (!servNombre) {
      setServNombre(value);
    }

    // Si el servicio tiene un precio base configurado, se lo asignamos
    if (option.precio_base) {
      setServPrecio(Number(option.precio_base));
    }
  };

  /**
   * @function ejecutarGuardadoEnEspera 
   * @author Alexis Ruiz Uribe
   * @date 14/02/2024 - Actualizado 02/05/2026
   * @param nombre
   * @resumen Función para ejecutar el guardado en espera
   */
  const ejecutarGuardadoEnEspera = (nombre) => {

    if (isPausarVenta) return;

    setIsPausarVenta(true);

    const payload = {
      nombre_referencia: nombre,
      carrito: carrito,
      total: sumaTotalGlobal
    };

    if (ventaActivaEnEspera?.id) {
      payload.id = ventaActivaEnEspera.id;
    }

    saveEspera.mutate(payload, {
      onSuccess: () => {
        vaciarCarrito();
        setVentaActivaEnEspera(null);
        setIsModalPausarOpen(false);
        setNombreReferenciaPausar('');
        message.success(`Venta de "${nombre}" guardada`);
        if (isMobile) setDrawerCarritoVisible(false);
        setIsPausarVenta(false);
      },
      onError: () => {
        setIsPausarVenta(false);
        message.error("Error al guardar en la nube");
      }
    });
  };

  const showModal = () => {
    if (sumaTotalGlobal !== 0) {
      if (isMobile) setDrawerCarritoVisible(false);

      if (modoOperacion === 'pedido') {
        // Validar si NO hay un cliente real seleccionado
        if (!clienteSeleccionado || clienteSeleccionado.id === 'DEFAULT') {
          message.warning("Debes seleccionar un cliente para registrar el pedido.");
          setIsModalSeleccionarClientePedidoOpen(true);
          return;
        }
        // Si hay un cliente válido seleccionado, abre el modal de confirmación del pedido
        setIsModalConfirmarPedidoOpen(true);
      } else {
        setIsModalOpen(true);
      }
    }
  };

  // Se utiliza para eliminar la card del flatlist
  useEffect(() => {
    if (carritoActualizado !== '') {
      setCarrito(carritoActualizado);
    }
  }, [carritoActualizado]);

  /**
   * @function obtenerProductosCategoria 
   * @author Alexis Ruiz Uribe
   * @date 14/02/2024
   * @param data
   * @resumen Función para obtener un nuevo array de productos por categoria
   * @modificado 11/06/2026 por cambios en el proceso de renderizado.
   */
  const obtenerProductosCategoria = (cat) => {
    setCategoriaSeleccionada(cat);
  }

  const totalArticulos = carrito.reduce((acc, item) => acc + Number(item.numItems), 0);

  // INTERFAZ DEL CARRITO
  const renderCarritoUI = () => (
    <>
      <div style={{ padding: '5px 10px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong style={{ color: '#1e293b', fontSize: '15px' }}>Carrito de Venta</Text>
        <Space size="middle">
          {isMobile && (
            <Button type="text" icon={<DownOutlined />} onClick={() => setDrawerCarritoVisible(false)} />
          )}
          <Button
            type="primary"
            icon={<SwapOutlined style={{ fontSize: '16px' }} />}
            onClick={pausarVenta}
            disabled={carrito.length === 0}
            style={{
              height: '38px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              background: '#F59E0B',
              borderColor: '#F59E0B',
              color: '#fff',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: carrito.length === 0 ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.2)'
            }}
          >
            Pausar
          </Button>
        </Space>
      </div>

      {seccionActual === 'principal' && (
        <div style={{
          padding: '6px 14px',
          background: '#f1f5f9',
          borderBottom: '1px solid #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
            <IoPricetagsOutline style={{ color: '#64748b', fontSize: '13px' }} />
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Tarifa:</span>
          </div>

          <div style={{ flex: 1 }}>
            <Select
              value={(clienteSeleccionado.id !== 'DEFAULT' && clienteSeleccionado.lista_precio_id) ? clienteSeleccionado.lista_precio_id : listaPrecioActiva}
              onChange={setListaPrecioActiva}
              disabled={clienteSeleccionado.id !== 'DEFAULT' && clienteSeleccionado.lista_precio_id}
              variant="borderless"
              style={{ width: '100%', height: '26px' }}
              popupMatchSelectWidth={false}
              className="select-tarifa-compacto"
              options={[
                { value: 'DEFAULT', label: 'Tarifa General' },
                ...Object.entries(config?.config_tarifas || {})
                  .filter(([key, data]) => data.activa)
                  .map(([key, data]) => ({
                    value: key,
                    label: data.nombre
                  }))
              ]}
            />
          </div>

          {/* Indicador visual de bloqueo */}
          {clienteSeleccionado.id !== 'DEFAULT' && clienteSeleccionado.lista_precio_id && (
            <span style={{ fontSize: '10px', color: '#059669', fontWeight: '800', backgroundColor: '#ecfdf5', padding: '1px 5px', borderRadius: '4px', border: '1px solid #a7f3d0', letterSpacing: '0.5px' }}>
              FIJA
            </span>
          )}
        </div>
      )}

      {ventaActivaEnEspera && (
        <div style={{ background: '#fff7ed', padding: '4px 10px', borderBottom: '1px solid #ffedd5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ color: '#EF4444', fontSize: '11px' }}>EDITANDO: {ventaActivaEnEspera.nombre_referencia}</Text>
          <Button type="text" size="small" style={{ fontSize: '10px', background: '#EF4444', color: '#fff' }} onClick={() => setVentaActivaEnEspera(null)}>Desvincular</Button>
        </div>
      )}

      <div className='divCarritoInfinite' id="scrollableDiv" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <List
          dataSource={carrito}
          renderItem={(item) => (
            <List.Item style={{ padding: '4px 0', border: 'none' }}>
              <ItemCardCarrito
                id={item.id}
                nombre={item.nombre}
                numItems={item.numItems}
                setCarrito={setCarrito}
                vaciarCarrito={vaciarCarrito}
                precioVenta={item.precioVenta || 0}
                stock={item.stock}
                descuento={item.descuento}
                precioCosto={Number(item.precio_compra || 0)}
                permitirEdicionPrecio={puedeEditar}
                controlarStock={controlarStock}
              />
            </List.Item>
          )}
        />
      </div>

      {/* Resumen de totales */}
      <div style={{ padding: '10px' }}>

        <div style={{ borderTop: '2px solid #f1f5f9', marginBottom: '10px', position: 'relative' }}>
          <span style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: '#f8fafc', padding: '0 10px', fontSize: '10px', color: '#cbd5e1', fontWeight: '700' }}>RESUMEN</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', background: '#fff', padding: '6px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
              <Text style={{ fontSize: '11px', fontWeight: '800', color: '#0369a1' }}>{totalArticulos} {totalArticulos === 1 ? 'ITEM' : 'ITEMS'}</Text>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', background: '#e0f2fe', padding: '6px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
              <Text style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b' }}>
                <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '400', marginRight: '4px' }}>SUBTOTAL:</span>
                ${subtotal.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', background: '#fff', padding: '6px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>Impuestos:</Text>
              <Text style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>${impuestos.toLocaleString('es-CO', { maximumFractionDigits: 0 })}</Text>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>descuento:</Text>
              <Text style={{ fontSize: '12px', fontWeight: '700', color: '#16a34a' }}>${totalDescuento.toLocaleString('es-CO')}</Text>
            </div>
          </div>
        </div>

        <div className='divTotal' style={{ marginBottom: '10px', minHeight: '60px' }}>
          <span className='divTotalTitle'>TOTAL</span>
          <span className='divTotalPrecio'>${sumaTotalGlobal.toLocaleString('es-CO')}</span>
        </div>

        <div className='divBotonesCarrito'>
          <Button className='btnCarritoCancelar' onClick={() => vaciarCarrito()}>
            <span className='btnTitle'>VACIAR</span>
          </Button>
          <Button
            className='btnCarritoVender'
            onClick={showModal}
            style={{
              backgroundColor: modoOperacion === 'pedido' ? '#f59e0b' : '#22BABB',
              borderColor: modoOperacion === 'pedido' ? '#f59e0b' : '#22BABB'
            }}
          >
            <span className='btnTitle'>
              {modoOperacion === 'pedido' ? 'GENERAR PEDIDO' : 'COBRAR'}
            </span>
          </Button>
        </div>
      </div>
    </>
  );

  if (modoOperacion === 'venta') {
    if (isLoadingCaja || cargandoUsuarios) return <VentanaVerificandoCaja />;
    if (!cajaActiva) return <VentanaCajaCerrada />;
  }

  return (
    <ConfigProvider locale={es_ES}>
      <LayoutHome style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>

        {/** Modal de cierre de venta */}
        <ModalCobrar
          digitoComprobante={digitoComprobante}
          setDigitoComprobante={setDigitoComprobante}
          clienteSeleccionado={clienteSeleccionado}
          cajaActiva={cajaActiva}
          cambio={cambio}
          efectivo={efectivo}
          sumaTotalGlobal={sumaTotalGlobal}
          carrito={carrito}
          setDatosImpresion={setDatosImpresion}
          subtotal={subtotal}
          impuestos={impuestos}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          totalArticulos={totalArticulos}
          totalDescuento={totalDescuento}
          handlePrint={handlePrint}
          setClienteSeleccionado={setClienteSeleccionado}
          ventaActivaEnEspera={ventaActivaEnEspera}
          deleteEspera={deleteEspera}
          vaciarCarrito={vaciarCarrito}
          setEfectivo={setEfectivo}
          setCambio={cambio}
          setIsModalClienteOpen={setIsModalClienteOpen}
          config={config}
          setListaPrecioActiva={setListaPrecioActiva}
          ordenIdOrigen={ordenIdOrigen}
        />

        <div className='divContainerProductos'>
          <div className='divProductos' style={{ padding: '15px' }}>
            {/* ENCABEZADO SUPERIOR SIMÉTRICO Y COMPACTO */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '10px', marginBottom: '12px' }}>

              {/* CONTENEDOR PRINCIPAL CON FLEX REORDENABLE */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: '8px',
                width: '100%'
              }}>

                {/* 1. SWITCHER VENTA / PEDIDO (Order 1 en Móvil / Order 1 en Web) */}
                {mostrarSwitcherPedidos && (
                  <div style={{
                    display: 'flex',
                    background: modoOperacion === 'pedido' ? '#fff7ed' : '#f1f5f9',
                    padding: '3px',
                    borderRadius: '10px',
                    border: modoOperacion === 'pedido' ? '1px solid #f59e0b' : '1px solid #cbd5e1',
                    flexShrink: 0,
                    order: isMobile ? 1 : 1
                  }}>
                    <Button
                      type="text"
                      size={isMobile ? "middle" : "small"}
                      onClick={() => setModoOperacion('venta')}
                      style={{
                        flex: isMobile ? 1 : 'none',
                        height: isMobile ? '36px' : '32px',
                        borderRadius: '8px',
                        fontWeight: '700',
                        fontSize: '12px',
                        backgroundColor: modoOperacion === 'venta' ? '#22BABB' : 'transparent',
                        color: modoOperacion === 'venta' ? '#ffffff' : '#64748b'
                      }}
                    >
                      🛒 Venta Directa
                    </Button>
                    <Button
                      type="text"
                      size={isMobile ? "middle" : "small"}
                      onClick={() => {
                        setModoOperacion('pedido');
                        if (!clienteSeleccionado || clienteSeleccionado.id === 'DEFAULT') {
                          setIsModalSeleccionarClientePedidoOpen(true);
                        }
                      }}
                      style={{
                        flex: isMobile ? 1 : 'none',
                        height: isMobile ? '36px' : '32px',
                        borderRadius: '8px',
                        fontWeight: '700',
                        fontSize: '12px',
                        backgroundColor: modoOperacion === 'pedido' ? '#f59e0b' : 'transparent',
                        color: modoOperacion === 'pedido' ? '#ffffff' : '#64748b'
                      }}
                    >
                      📝 Toma de Pedido
                    </Button>
                  </div>
                )}

                {/* 2. BARRA CLIENTE Y ZONA (Order 2 en Móvil / Order 3 en Web) */}
                {modoOperacion === 'pedido' && (
                  <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: isMobile ? '100%' : 'auto',
                    order: isMobile ? 2 : 3,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                      <div style={{
                        background: '#dcfce7',
                        borderRadius: '8px',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <UserOutlined style={{ color: '#16a34a', fontSize: '16px' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Text strong style={{ color: '#14532d', fontSize: '13px', lineHeight: '1.2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                          {clienteSeleccionado?.id !== 'DEFAULT' ? (clienteSeleccionado?.nombre_comercial || clienteSeleccionado?.nombre) : 'Sin seleccionar'}
                        </Text>
                        <Text style={{ fontSize: '11px', color: '#16a34a', lineHeight: '1.2' }}>
                          📍 {zonaActiva?.nombre || 'Todas las zonas'}
                        </Text>
                      </div>
                    </div>

                    <Button
                      size="small"
                      type="default"
                      onClick={() => setIsModalSeleccionarClientePedidoOpen(true)}
                      style={{
                        marginLeft: '10px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#15803d',
                        borderColor: '#86efac',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      Cambiar
                    </Button>
                  </div>
                )}

                {/* 3. BUSCADOR INTEGRADO + BOTÓN SERVICIOS (Order 3 en Móvil / Order 2 en Web) */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto',
                  flex: isMobile ? 'none' : 1,
                  alignItems: 'center',
                  order: isMobile ? 3 : 2
                }}>
                  <Input
                    ref={searchInputRef}
                    placeholder="🔍 Buscar o escanear código..."
                    autoFocus={!isMobile}
                    onChange={(e) => {
                      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                      searchTimeoutRef.current = setTimeout(() => {
                        setBusqueda(e.target.value);
                      }, 300);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                        const codigoEscaner = e.target.value.trim();
                        if (!codigoEscaner) return;

                        let productoMatch = null;
                        let varianteMatch = null;

                        for (const p of productosActivos) {
                          if (p.codigo_barras?.toString() === codigoEscaner || p.codigo_interno?.toString() === codigoEscaner) {
                            productoMatch = p;
                            break;
                          }
                          if (p.has_variants && p.variantes) {
                            const varFound = p.variantes.find(v => v.codigo_barras?.toString() === codigoEscaner || v.codigo_interno?.toString() === codigoEscaner);
                            if (varFound) {
                              productoMatch = p;
                              varianteMatch = varFound;
                              break;
                            }
                          }
                        }

                        if (productoMatch) {
                          procesarSeleccionProducto(productoMatch, varianteMatch);
                          manejarCambioBusqueda('');
                          if (searchInputRef.current && searchInputRef.current.input) {
                            searchInputRef.current.input.value = '';
                            searchInputRef.current.focus();
                          }
                          if (categoriaSeleccionada !== 'Todos') setCategoriaSeleccionada('Todos');
                        } else {
                          message.error("Producto no encontrado. Revisa el código.");
                          if (searchInputRef.current && searchInputRef.current.input) {
                            searchInputRef.current.input.value = '';
                          }
                          manejarCambioBusqueda('');
                        }
                      }
                    }}
                    style={{
                      flex: 1,
                      height: isMobile ? '44px' : '40px',
                      fontSize: '14px',
                      borderRadius: '10px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      paddingLeft: '11px'
                    }}
                  />

                  {modoOperacion === 'venta' && (
                    <Button
                      type="primary"
                      icon={<ToolOutlined style={{ fontSize: '18px' }} />}
                      onClick={handleAbrirServicio}
                      title="Servicios"
                      style={{
                        height: isMobile ? '44px' : '40px',
                        width: isMobile ? '44px' : '40px',
                        minWidth: isMobile ? '44px' : '40px',
                        borderRadius: '10px',
                        backgroundColor: (config.modulo_servicios_activo === true || config.modulo_servicios_activo === 'true') ? '#3b82f6' : '#94a3b8',
                        borderColor: 'transparent',
                        display: 'flex',
                        justify: 'center',
                        alignItems: 'center',
                        flexShrink: 0
                      }}
                    />
                  )}
                </div>

                {/* 4. PESTAÑAS DE NAVEGACIÓN EN MODO VENTA (Order 4 en Móvil / Order 4 en Web) */}
                {modoOperacion === 'venta' && (
                  <div style={{
                    display: 'flex',
                    background: '#f1f5f9',
                    padding: '3px',
                    borderRadius: '10px',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: isMobile ? 'space-between' : 'flex-start',
                    flexShrink: 0,
                    order: 4
                  }}>
                    {[
                      { id: 'principal', label: 'Ventas', icon: <ThunderboltOutlined /> },
                      { id: 'espera', label: `Espera`, icon: <SwapOutlined />, extra: ventasEnEspera.length > 0 ? (<span style={{ marginLeft: '4px', background: seccionActual === 'espera' ? '#fff' : '#22BABB', color: seccionActual === 'espera' ? '#22BABB' : '#fff', padding: '0px 5px', borderRadius: '8px', fontSize: '10px' }}>{ventasEnEspera.length}</span>) : null },
                      hasPermission('ventas:historial_ventas') ? { id: 'lista', label: 'Historial', icon: <FileTextOutlined /> } : null
                    ]
                      .filter(Boolean)
                      .map((boton) => (
                        <Button
                          key={boton.id}
                          type="text"
                          size="small"
                          onClick={() => setSeccionActual(boton.id)}
                          style={{
                            flex: isMobile ? 1 : 'none',
                            height: isMobile ? '36px' : '34px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '12px',
                            backgroundColor: seccionActual === boton.id ? '#22BABB' : 'transparent',
                            color: seccionActual === boton.id ? '#ffffff' : '#64748b'
                          }}
                          icon={boton.icon}
                        >
                          {boton.label} {boton.extra}
                        </Button>
                      ))}
                  </div>
                )}
              </div>

            </div>

            {/* SECCIÓN DE CATEGORÍAS (Súper compacta) */}
            {seccionActual === 'principal' && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  className="categorias-scroll-container"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    textTransform: 'uppercase'
                  }}
                >
                  {categoriasUnicas.map((item) => {
                    const esActivo = categoriaSeleccionada === item;
                    return (
                      <div
                        key={item}
                        onClick={() => obtenerProductosCategoria(item)}
                        style={{
                          padding: '0 14px',
                          height: '38px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '700',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease',
                          backgroundColor: esActivo ? '#22BABB' : '#ffffff',
                          color: esActivo ? '#ffffff' : '#475569',
                          border: esActivo ? '1px solid #22BABB' : '1px solid #e2e8f0',
                          boxShadow: esActivo
                            ? '0 4px 6px -1px rgba(34, 186, 187, 0.25)'
                            : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        {item}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {seccionActual === 'principal' && <SeccionVentasPrincipal procesarSeleccionProducto={procesarSeleccionProducto} productoCategoria={productosFiltrados} categoriaSeleccionada={categoriaSeleccionada} setCarrito={setCarrito} setBusqueda={manejarCambioBusqueda} productosDB={productosActivos} />}
            {seccionActual === 'espera' && <SeccionVentasEspera carrito={carrito} setCarrito={setCarrito} setVentaActivaEnEspera={setVentaActivaEnEspera} ventasEnEspera={ventasEnEspera} saveEspera={saveEspera} deleteEspera={deleteEspera} setSeccionActual={setSeccionActual} />}
            {seccionActual === 'lista' && <SeccionListaVentas cajaActiva={cajaActiva} setDatosImpresion={setDatosImpresion} handlePrint={handlePrint} config={config} />}

          </div>

          {/* CARRITO LATERAL (Solo visible en Desktop) */}
          {!isMobile && (
            <div className='divCarrito' style={{ height: '100%', overflow: 'hidden' }}>
              {renderCarritoUI()}
            </div>
          )}
        </div>
      </LayoutHome>

      {/* DRAWER DEL CARRITO (Solo visible en Móvil) */}
      <Drawer
        placement="bottom"
        height="85vh"
        open={drawerCarritoVisible}
        onClose={() => setDrawerCarritoVisible(false)}
        className="drawer-carrito-mobile"
        closeIcon={null}
        title={null}
      >
        {renderCarritoUI()}
      </Drawer>

      {/* NAVBAR INFERIOR FLOTANTE (Solo visible en Móvil) */}
      {isMobile && seccionActual === 'principal' && (
        <div className="bottom-cart-bar" onClick={() => setDrawerCarritoVisible(true)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: '#ffffff', color: '#22BABB', borderRadius: '50%', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingCartOutlined style={{ fontSize: '18px' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '12px', fontWeight: '600' }}>Ver Carrito</span>
              <span style={{ fontSize: '11px', opacity: 0.9 }}>{totalArticulos} artículos añadidos</span>
            </div>
          </div>
          <div style={{ fontSize: '18px', fontWeight: '800' }}>
            ${sumaTotalGlobal.toLocaleString('es-CO')}
          </div>
        </div>
      )}

      {/* MODAL SELECCIÓN DE VARIANTES */}

      <ModalVariantes
        isModalVariantesOpen={isModalVariantesOpen}
        setIsModalVariantesOpen={setIsModalVariantesOpen}
        productoParaVariantes={productoParaVariantes}
        procesarSeleccionProducto={procesarSeleccionProducto}
      />

      <ModalServicios
        isModalServicioOpen={isModalServicioOpen}
        setIsModalServicioOpen={setIsModalServicioOpen}
        agregarServicioAlCarrito={agregarServicioAlCarrito}
        servTipo={servTipo}
        handleSelectServicio={handleSelectServicio}
        catalogoServicios={catalogoServicios}
        servNombre={servNombre}
        setServNombre={setServNombre}
        servCosto={servCosto}
        setServCosto={setServCosto}
        servPrecio={servPrecio}
        setServPrecio={setServPrecio}
        servMargen={servMargen}
        servGanancia={servGanancia}
      />

      <ModalPausarVenta
        isModalPausarOpen={isModalPausarOpen}
        setIsModalPausarOpen={setIsModalPausarOpen}
        nombreReferenciaPausar={nombreReferenciaPausar}
        setNombreReferenciaPausar={setNombreReferenciaPausar}
        ejecutarGuardadoEnEspera={ejecutarGuardadoEnEspera}
        isGuardando={isGuardandoEnEspera}
      />

      <ModalClienteRapido
        isModalClienteOpen={isModalClienteOpen}
        setClienteSeleccionado={setClienteSeleccionado}
        setIsModalClienteOpen={setIsModalClienteOpen}
      />

      <ModalSeleccionarClientePedido
        isOpen={isModalSeleccionarClientePedidoOpen}
        onClose={() => setIsModalSeleccionarClientePedidoOpen(false)}
        zonaActiva={zonaActiva}
        setZonaActiva={setZonaActiva}
        clienteSeleccionado={clienteSeleccionado}
        setClienteSeleccionado={setClienteSeleccionado}
      />

      <ModalConfirmarPedido
        isOpen={isModalConfirmarPedidoOpen}
        setIsOpen={setIsModalConfirmarPedidoOpen}
        clienteSeleccionado={clienteSeleccionado}
        zonaActiva={zonaActiva}
        carrito={carrito}
        sumaTotalGlobal={sumaTotalGlobal}
        totalArticulos={totalArticulos}
        subtotal={subtotal}
        impuestos={impuestos}
        totalDescuento={totalDescuento}
        vaciarCarrito={vaciarCarrito}
        config={config}
      />

      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={componentRef} style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', padding: '5px 2px 20px 2px', backgroundColor: '#F0F2F4' }}>
          {datosImpresion && (
            <TicketVenta
              carrito={datosImpresion.carrito}
              totales={datosImpresion.totales}
              ventaId={datosImpresion.ventaId}
              numeroFactura={datosImpresion.numeroFactura}
              fechaVenta={datosImpresion.fechaVenta}
              clienteNombre={datosImpresion.clienteNombre}
              clienteDocumento={datosImpresion.clienteDocumento}
              clienteTelefono={datosImpresion.clienteTelefono}
              clienteDireccion={datosImpresion.clienteDireccion}
              cajero={datosImpresion.cajero || "Cajero(a)"}
              metodoPago={datosImpresion.metodoPago}
              cambioEntregado={datosImpresion.cambioEntregado}
              efectivoRecibido={datosImpresion.efectivoRecibido}
              montoTransferencia={datosImpresion.montoTransferencia}
              infoNegocio={{
                nombre: config.nombre,
                nombre_legal: config.nombre_legal,
                nit: config.identificacion_fiscal,
                direccion: config.direccion,
                telefono: config.telefono,
                // También corregido aquí para que la tirilla sepa si es 57mm u 80mm
                anchoTicket: config.impresora_tipo || config.settings?.impresora_tipo || '57mm',
                logo: config.logo
              }}
            />
          )}
        </div>
      </div>
    </ConfigProvider>
  )
}

export default Ventas;